import usePrivateAxios from "@/common/api/usePrivateAxios";
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant";
import { Mapper } from "@/common/services/mapper";
import { MetaResponse } from "@/common/types/metaResponse";
import { InfiniteData, keepPreviousData, useInfiniteQuery, UseInfiniteQueryOptions } from "@tanstack/react-query";

type UseMaintenanceEquipmentPiecesInfiniteProps<T> = {
  equipmentId: string | undefined;
  filters?: {
    search?: string;
    partId?: string;
  };
  options?: Partial<UseInfiniteQueryOptions<any, any, InfiniteData<any>>>;
  mapper?: (data: any) => T;
}

export const useMaintenanceEquipmentPiecesInfinite = <T,>({
  equipmentId,
  filters: { search = '', partId } = {},
  options,
  mapper = Mapper.default<T>(),
}: UseMaintenanceEquipmentPiecesInfiniteProps<T>) => {
  const privateAxios = usePrivateAxios();

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    ...rest
  } = useInfiniteQuery({
    enabled: !!equipmentId,
    queryKey: ['maintenanceEquipmentPiecesInfinite', equipmentId, search, partId],
    queryFn: ({ pageParam }) => {
      return privateAxios.get(`/client/equipments/${equipmentId}/maintenance_pieces`, {
        params: {
          search,
          page: pageParam,
          page_size: 10,
          'q[part_id_eq]': partId,
        },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: { data: { data: any[]; meta: MetaResponse } }) => {
      if (lastPage?.data?.meta?.current_page < lastPage?.data?.meta?.total_pages) {
        return lastPage.data.meta.current_page + 1;
      }
    },
    placeholderData: keepPreviousData,
    ...options,
    select: (data) => {
      const flatData = data?.pages?.reduce((acc, page) => {
        return [...acc, ...page?.data?.data];
      }, [] as any[]);

      return {
        data: mapper(flatData) as T,
        meta: data?.pages[data?.pages?.length - 1]?.data?.meta as MetaResponse,
      };
    },
  });

  return { data, meta, ...rest };
};
